import { LegalPageLayout, P, UL, LI, Strong, ContactLink } from './LegalPageLayout';

/**
 * Service Level Agreement for PlyEZ (plyez.com). Defines the 99.9% monthly
 * uptime target for Enterprise plans, how uptime and downtime are measured,
 * exclusions, the service-credit schedule, and how to claim. Effective
 * 2026-06-03. We recommend reviewing it with your own counsel; it reflects
 * our current practices.
 */

const CREDITS: { uptime: string; downtime: string; credit: string }[] = [
  { uptime: '< 99.9% and ≥ 99.5%', downtime: '~44 min to 3h 39m', credit: '10%' },
  { uptime: '< 99.5% and ≥ 99.0%', downtime: '~3h 39m to 7h 18m', credit: '15%' },
  { uptime: '< 99.0% and ≥ 95.0%', downtime: '~7h 18m to 36h 30m', credit: '25%' },
  { uptime: '< 95.0%', downtime: 'over ~36h 30m', credit: '50%' },
];

export function SlaPage() {
  return (
    <LegalPageLayout
      i18nKey="sla"
      lastUpdated="2026-06-03"
      lead={
        <>
          This Service Level Agreement (the &ldquo;SLA&rdquo;) describes the
          availability commitment for <Strong>PlyEZ</Strong> and the service credits
          that apply if we miss it. It is part of, and governed by, the{' '}
          <ContactLink to="/legal/terms">Terms of Service</ContactLink>. Unless an
          Order or signed Master Service Agreement states otherwise, the uptime
          commitment and service credits in this SLA apply to{' '}
          <Strong>Enterprise</Strong> plans. Live and historical availability is
          published at{' '}
          <a className="text-accent-700 underline" href="https://status.plyez.com" target="_blank" rel="noreferrer">status.plyez.com</a>.
        </>
      }
      sections={[
        {
          id: 'commitment',
          title: '1. Availability commitment',
          body: (
            <>
              <P>
                We will use commercially reasonable efforts to make the PlyEZ web
                application and API (the &ldquo;Covered Service&rdquo;) available with a{' '}
                <Strong>Monthly Uptime Percentage of at least 99.9%</Strong> during each
                calendar month. The remedy for failing to meet this commitment is the
                service credit described in Section 4.
              </P>
            </>
          ),
        },
        {
          id: 'definitions',
          title: '2. Definitions',
          body: (
            <>
              <UL>
                <LI><Strong>&ldquo;Monthly Uptime Percentage&rdquo;</Strong> — for a calendar month, total minutes in the month minus Downtime minutes, divided by total minutes in the month, expressed as a percentage.</LI>
                <LI><Strong>&ldquo;Downtime&rdquo;</Strong> — sustained periods during which the Covered Service is unavailable to perform core operations (sign-in, reading and writing records, and submitting requests through the API), as confirmed by our monitoring, excluding the events in Section 3. Brief transient errors are measured over consecutive intervals, not single failed requests.</LI>
                <LI><Strong>&ldquo;Scheduled Maintenance&rdquo;</Strong> — maintenance announced at least 72 hours in advance via status.plyez.com and email.</LI>
                <LI><Strong>&ldquo;Emergency Maintenance&rdquo;</Strong> — urgent maintenance required to protect the security, integrity, or stability of the Service, performed with as much notice as is practicable.</LI>
                <LI><Strong>&ldquo;Service Credit&rdquo;</Strong> — a percentage of the monthly subscription fee for the affected Covered Service, applied as a credit against a future invoice.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'exclusions',
          title: '3. Exclusions',
          body: (
            <>
              <P>
                The availability commitment does not apply to, and the following are
                excluded from Downtime:
              </P>
              <UL>
                <LI>Scheduled Maintenance and Emergency Maintenance;</LI>
                <LI>force majeure and other events beyond our reasonable control, including natural disasters, network or power outages outside our infrastructure, and acts of government;</LI>
                <LI>failures of third-party services, networks, or equipment outside our control — including your bank, your network or devices, identity providers, or upstream payment and AI providers;</LI>
                <LI>suspension or termination of your account, or throttling, in accordance with the Terms of Service or Acceptable Use Policy;</LI>
                <LI>issues caused by your misuse, misconfiguration, or use of the Service contrary to the documentation, and features labeled beta, preview, or early access;</LI>
                <LI>the latency or accuracy of AI Features, which are assistive and not subject to this uptime commitment.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'credits',
          title: '4. Service-credit schedule',
          body: (
            <>
              <P>
                If the Monthly Uptime Percentage for the Covered Service falls below
                99.9% in a calendar month, you are eligible for a Service Credit equal
                to the following percentage of that month&rsquo;s subscription fee for
                the affected Covered Service:
              </P>
              <div className="overflow-x-auto -mx-2 mb-4">
                <table className="w-full text-sm border border-border rounded-lg">
                  <thead>
                    <tr className="bg-muted/40 text-left">
                      <th className="px-3 py-2 font-medium">Monthly Uptime Percentage</th>
                      <th className="px-3 py-2 font-medium">Approx. monthly Downtime</th>
                      <th className="px-3 py-2 font-medium num">Service Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CREDITS.map((row) => (
                      <tr key={row.uptime} className="border-t border-border align-top">
                        <td className="px-3 py-2 font-medium num">{row.uptime}</td>
                        <td className="px-3 py-2 text-foreground/80 num">{row.downtime}</td>
                        <td className="px-3 py-2 text-foreground/80 num">{row.credit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <P>
                Service Credits are your sole and exclusive remedy for any failure to
                meet the availability commitment. The total Service Credits awarded for
                any calendar month will not exceed 50% of that month&rsquo;s
                subscription fee for the affected Covered Service. Credits are applied to
                a future invoice, have no cash value, and are not refundable. Credits are
                not available while your account has overdue undisputed amounts.
              </P>
            </>
          ),
        },
        {
          id: 'claim',
          title: '5. How to claim a credit',
          body: (
            <>
              <P>
                To request a Service Credit, email{' '}
                <a className="text-accent-700 underline" href="mailto:support@plyez.com">support@plyez.com</a>{' '}
                with the subject line &ldquo;SLA Credit Request&rdquo; within 30 days
                after the end of the month in which the Downtime occurred. Include the
                affected workspace, the dates and times of the Downtime, and any logs or
                error details that help us confirm the impact.
              </P>
              <P>
                We will validate the claim against our monitoring and the public record
                at{' '}
                <a className="text-accent-700 underline" href="https://status.plyez.com" target="_blank" rel="noreferrer">status.plyez.com</a>{' '}
                and respond within 30 days. Failing to submit a request within the 30-day
                window forfeits the credit for that month.
              </P>
            </>
          ),
        },
        {
          id: 'operations',
          title: '6. Operational practices',
          body: (
            <>
              <P>
                Beyond the availability commitment, our standard operational posture for
                Enterprise plans includes:
              </P>
              <UL>
                <LI><Strong>Recovery objectives.</Strong> A Recovery Point Objective (RPO) of 24 hours and a Recovery Time Objective (RTO) of 4 hours for a region-level failure.</LI>
                <LI><Strong>Backups and retention.</Strong> Daily encrypted backups retained on a 35-day rolling basis; documents covered by the Electronic Books Preservation Act (電子帳簿保存法) are retained for 7 years past period-end.</LI>
                <LI><Strong>Incident response.</Strong> Acknowledgment targets of under 1 hour for P1 and under 4 hours for P2 incidents, with status updates posted to status.plyez.com.</LI>
                <LI><Strong>Region pinning.</Strong> JP-resident customers can pin data and AI processing to a Japan region, and Enterprise customers can pin AI routing to a single provider for data-residency posture.</LI>
                <LI><Strong>Security.</Strong> Encryption at rest (AES-256-GCM) and in transit (TLS 1.3), with a tamper-evident audit log; SOC 2 and ISO 27001 (ISMS) programs are in progress and not yet certified.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'changes',
          title: '7. Changes and contact',
          body: (
            <>
              <P>
                We may update this SLA from time to time; material changes are announced
                as described in the{' '}
                <ContactLink to="/legal/terms">Terms of Service</ContactLink>, and the
                current version and effective date are always shown on this page.
                Questions about availability or a credit request go to{' '}
                <a className="text-accent-700 underline" href="mailto:support@plyez.com">support@plyez.com</a>.
              </P>
            </>
          ),
        },
      ]}
    />
  );
}
